import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { InfoIcon } from "./IconInfo";
import { RegionIcon } from "./IconRegion";
import { NationalIcon } from "./IconNational";
import { DistrictProgramsSection } from "./DistrictProgramsSection";
import { EducationSection } from "./EducationSection";
import { PanelContainer, SectionHeader, SalaryAmount } from "./styles";
import type {
  MSHatData,
  RegionSalaryData,
  DistrictProgramForHat,
} from "../../types";

interface BackRightPanelProps {
  hat: MSHatData;
  selectedRegionSalary?: RegionSalaryData;
  nationalSalary?: RegionSalaryData;
  /** District programs matching this hat. Schools are pre-filtered server-side. */
  districtPrograms?: DistrictProgramForHat[];
  /** When true, skip the "See Your Matches" button + animation in the district programs section. */
  districtMatchesRevealed?: boolean;
  /** Fires once the calculating animation in the district programs section completes. */
  onDistrictMatchesRevealed?: () => void;
}

const formatSalary = (value?: number): string => {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

export const BackRightPanel = ({
  hat,
  selectedRegionSalary,
  nationalSalary,
  districtPrograms,
  districtMatchesRevealed,
  onDistrictMatchesRevealed,
}: BackRightPanelProps) => {
  const hasDistrictPrograms = districtPrograms && districtPrograms.length > 0;

  return (
    <PanelContainer>
      {/* Average Salary */}
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 1, flexShrink: 0 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <SectionHeader sx={{ width: "auto" }}>Average Salary</SectionHeader>
          <Tooltip title="Salary data is based on regional averages and may vary">
            <Box sx={{ cursor: "pointer", display: "flex" }}>
              <InfoIcon />
            </Box>
          </Tooltip>
        </Box>

        <Box sx={{ display: "flex", gap: 4, justifyContent: "center" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, height: 24 }}
            >
              <RegionIcon />
              <Typography
                sx={{
                  fontFamily: "Proxima Nova, sans-serif",
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "22px",
                  color: "#d3fcd2",
                  whiteSpace: "nowrap",
                }}
              >
                Your Region
              </Typography>
            </Box>
            <SalaryAmount>
              {formatSalary(selectedRegionSalary?.payRange?.mean)}
            </SalaryAmount>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, height: 24 }}
            >
              <NationalIcon />
              <Typography
                sx={{
                  fontFamily: "Proxima Nova, sans-serif",
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "22px",
                  color: "#efd6ff",
                  whiteSpace: "nowrap",
                }}
              >
                National
              </Typography>
            </Box>
            <SalaryAmount>
              {formatSalary(nationalSalary?.payRange?.mean)}
            </SalaryAmount>
          </Box>
        </Box>
      </Box>

      {/* District programs (if any match), then education as fallback */}
      {hasDistrictPrograms && (
        <DistrictProgramsSection
          programs={districtPrograms!}
          revealed={districtMatchesRevealed}
          onRevealed={onDistrictMatchesRevealed}
        />
      )}
      {!hasDistrictPrograms && <EducationSection hat={hat} />}
    </PanelContainer>
  );
};
